import userOrderService from 'lib/services/userOrderService';
import {
  fetchAsyncDeliveryRecord,
  fetchReceiveLotteryPrize,
  fetchReceiveGift,
  fetchUserPrizeStatus,
  multipleLotteryRequest,
} from 'lib/apis';
import { awardFusionPrize } from 'lib/apis/nodeApi';
import TipsModal from 'lib/components/Modal/TipsModal';
import TipsModalV2 from 'lib/components/Modal/TipsModalV2';
import { showModal } from 'lib/components/Modal';
import { AWARD_PRIZE_TYPE, AWARD_TYPES, DELIVER_STATUS, PrizeSource } from 'lib/constants/award';
import { ERROR_MSG } from 'lib/constants/error';
import { getMobileGameRole } from 'lib/components/MobileGameRole';
import { handleCMCCPrizeReceive } from 'lib/components/ReceiveDialog/helper';
import { useAwardPrizeStore } from 'lib/store/awardPrize';
import { showAsyncDeliverDialog } from 'lib/components/AsyncDeliverDialog';
import { GiftDataType, ReceiveGiftRequestData } from 'lib/apis/model/gift';
import { GameRoleType } from 'lib/types/AMS';
import { showReceiveDone } from 'lib/container/ReceiveDone';
import { report } from 'lib/utils/dataReport';
import { ATTA_EVENT_RESULT, ATTA_REPORT } from 'lib/constants/atta';
import { getReceiveGiftReportKey } from 'lib/utils/util';
import event from 'lib/utils/event';
import { EVENT } from 'lib/utils/event/constant';
import { showOrderAddressInput } from 'lib/container/AddressInput';
import { showOrderJumpUrlModal } from 'lib/container/JumpUrl';
import SaveAddressSuccess from 'lib/container/AddressInput/SaveAddressSuccess';
import toast from 'lib/utils/toast';
import { BenefitRecordAwardStatus } from 'lib/constants/fragment';
import { showLotterySingleResultDialog } from 'lib/components/LotterySingleResultDialog';
import { receiveCash, receiveCdkey, receiveMarketingResourcePrize } from 'lib/utils/award';
import monitor from 'lib/utils/monitor';
import { showReceiveQBDialog } from 'lib/components/ReceiveQBDialog';
import { sigleDraw } from 'lib/utils/prize/lottery';
import { showTipsDialog } from 'lib/components/TipsDialog';
import channelService from './channelService';
import gameCardGuideService from './gameCardGuideService';

interface GiftData {
  actId: number;
  giftType: AWARD_TYPES;
}

interface QBDeliverParams {
  qqUin: string;
}

const RECEIVE_ERROR_CODE = {
  CMCC_NO_AWARD_CHANCE: 30201,
  CMCC_LIMIT: 301000,
};

class AwardPrizeService {
  private allGiftData: GiftData[] = [];
  private awardPrizeStore: any;

  constructor() {
    this.awardPrizeStore = useAwardPrizeStore.getState();
  }

  public getAllGiftData() {
    return this.allGiftData;
  }

  // 礼包领取状态
  public getGiftReceiveStatus(actId: number): boolean {
    return this.awardPrizeStore.getPrizeIsReceived(actId);
  }

  // 领取角色还是直接抽奖
  public async receiveGift(actId: number) {
    const giftInfo = this.allGiftData.find((c) => c.actId === actId);
    switch (giftInfo?.giftType) {
      // 必得礼包
      case AWARD_TYPES.MUST:
      case AWARD_TYPES.NORMAL: {
        // 触发领取角色弹窗

        this.displayMobileGameRole(this.receiveGiftSuccess.bind(this), { actId });
        return;
      }
      case AWARD_TYPES.RIGHTS: {
        // 执行抽奖逻辑
        const { prizeType, awardName, lotteryId, prizeUrl } =
          (await this.lotteryGiftRequest({ actId }, giftInfo.giftType)) || {};

        if (!prizeType) return;

        this.receiveLotteryGift({ actId, prizeType, awardName, lotteryId, prizeUrl });
        return;
      }
      case AWARD_TYPES.LOTTERY: {
        event.emit(EVENT.INVOKE_LOTTERY_START);
        // 执行抽奖逻辑
        const { prizeType, awardName, prizeId, lotteryId, prizeUrl } =
          (await this.lotteryGiftRequest({ actId }, giftInfo.giftType)) || {};

        if (!prizeType) return;
        event.emit(EVENT.INVOKE_LOTTERY_END, prizeId);
        event.on(EVENT.LOTTERY_ANIMATE_FINISH, () => {
          this.receiveLotteryGift({ actId, prizeType, awardName, prizeId, lotteryId, prizeUrl });
        });
        return;
      }
    }
  }

  public async registerGiftData(actId: number, btnType = AWARD_TYPES.NORMAL) {
    const isExist = this.allGiftData.filter((c) => c.actId === actId);
    if (isExist?.length) return;
    if (!+actId) return;
    this.allGiftData.push({
      actId,
      giftType: btnType,
    });
  }

  public async getWaitForReceiveGiftData(): Promise<number[]> {
    const actIds = this.allGiftData.map((item) => item.actId);
    const res = await Promise.all(actIds.map((item) => this.getUserPrizeStatus(item)));
    const receivedList: number[] = res.filter((c) => c) || [];

    this.awardPrizeStore.changePrizeStatus([...receivedList]);

    if (receivedList.length === this.allGiftData.length) {
      return [];
    }

    const waitForReceiveList = this.getRemainNeedReceiveList(receivedList);

    return waitForReceiveList.map((item) => item.actId);
  }

  // 连抽逻辑
  public async multipleLottery(actId: number, successCallback: (list: Array<Record<string, any>>) => void) {
    toast.loading();
    const res = await multipleLotteryRequest({ actId });
    toast.close();
    if (res?.code !== 0) {
      if ([RECEIVE_ERROR_CODE.CMCC_NO_AWARD_CHANCE, RECEIVE_ERROR_CODE.CMCC_LIMIT].includes(res?.code)) {
        this.handleReceiveGiftFail(res?.code, { actId }, true);
        return;
      }
      // 抽奖失败
      TipsModal.show({
        title: ERROR_MSG[res?.code] || '系统繁忙',
        content: ERROR_MSG[res?.code] ? '' : '请稍后重试，若有疑问请咨询福利鹅微信公众号客服～',
      });
      return;
    }
    successCallback(res.data?.drawResult);
  }

  // 领取连抽礼包，和日常礼包、单次抽奖礼包领取逻辑不一致，弹窗样式也不一致
  public async receiveMultipleLotteryGift(prizeInfo: Record<string, any>, successCallback: () => void) {
    const { prizeType, prizeName, actId, lotteryId, deliverStatus } = prizeInfo;

    // 0 未领取 1 已领取
    if (deliverStatus === DELIVER_STATUS.DELIVERED) {
      handleReceiveGiftSuccess();
      return;
    }

    switch (prizeType) {
      // 游戏道具
      case AWARD_PRIZE_TYPE.AMS: {
        // ams礼包领取
        const multipleLotteryAmsPrizeDelivery = async (params) => {
          const res = await fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
          if (res?.code !== 0) {
            report(ATTA_REPORT.LOTTERY_REACEIVE.AMS_RECEIVE_FAIL);
            TipsModal.show({
              title: ERROR_MSG[res?.code] || '礼包领取失败',
              content: ERROR_MSG[res?.code] ? '' : '请稍后重试',
            });
            return;
          }
          successCallback?.();
          report(ATTA_REPORT.LOTTERY_REACEIVE.AMS_RECEIVE_SUCCESS);
          return handleReceiveGiftSuccess();
        };
        this.displayMobileGameRole(multipleLotteryAmsPrizeDelivery, {});
        return;
      }

      // 实物奖品
      case AWARD_PRIZE_TYPE.GOODS: {
        report(ATTA_REPORT.LOTTERY_REACEIVE.GOODS_RECEIVEDIALOG_SHOW);
        showOrderAddressInput({ ...prizeInfo, successCallback });
        return;
      }

      // QB
      case AWARD_PRIZE_TYPE.QB: {
        // QB礼包领取
        const multipleLotteryQBPrizeDelivery = async (params: QBDeliverParams) =>
          fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
        showReceiveQBDialog({ deliverRequest: multipleLotteryQBPrizeDelivery, receiveCallback: successCallback });
        return;
      }

      case AWARD_PRIZE_TYPE.CASH: {
        receiveCash({ prizeInfo, callback: successCallback, prizeSource: PrizeSource.NOT });
        return;
      }

      case AWARD_PRIZE_TYPE.CDKEY: {
        receiveCdkey({ prizeInfo, callback: successCallback, actId, lotteryId });
        return;
      }
    }
    async function handleReceiveGiftSuccess() {
      const reportEvent = () => {
        report(ATTA_REPORT.MULTIPLE_LOTTERY.LOTTERY_CLOSE_MODAL);
      };
      switch (prizeType) {
        // 游戏道具
        case AWARD_PRIZE_TYPE.AMS: {
          TipsModal.show({
            title: '温馨提示',
            content: [`已成功领取${prizeName}`, '已发送至游戏邮箱，请在游戏内查收'],
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });
          return;
        }

        // 实物奖品
        case AWARD_PRIZE_TYPE.GOODS: {
          TipsModal.show({
            content: SaveAddressSuccess,
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });
          return;
        }

        case AWARD_PRIZE_TYPE.OUTER_LINK: {
          showOrderJumpUrlModal(prizeInfo);
          return;
        }

        case AWARD_PRIZE_TYPE.QB: {
          TipsModal.show({
            title: '温馨提示',
            content:
              deliverStatus === 1 ? ['已领取', '请前往领取的QQ号查看是否到账'] : ['24小时内Q币可到账', '请注意查收'],
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });
          return;
        }

        case AWARD_PRIZE_TYPE.FRAGMENT: {
          report(ATTA_REPORT.MULTIPLE_LOTTERY.LOTTERY_FRAGMENT_VIEW, {
            prizeId: prizeInfo.prizeId,
            prizeType: prizeInfo.prizeType,
          });
          showLotterySingleResultDialog({ title: '', confirmButtonText: '我知道了', prizeInfo, isTenDraw: true });
          return;
        }

        case AWARD_PRIZE_TYPE.MARKETING_RESOURCE: {
          receiveMarketingResourcePrize({
            prizeId: prizeInfo.prizeId,
          });
          return;
        }
      }
    }
  }

  // 合成礼包跟十连抽礼包类型领取方式一致，但是接口不一致 这里看是否直接改调用不同的接口，还是单独开辟一个地方
  // 碎片、合成奖励、普通的结合
  public async receiveCommonLotteryGift(prizeInfo: Record<string, any>, successCallback: () => void) {
    const {
      prizeType,
      prizeName,
      actId,
      lotteryId,
      deliverStatus,
      prizeSource,
      recordStatus,
      fusionPrizeRecordId,
      prizeId,
      productId,
    } = prizeInfo;
    const hasDelivered =
      deliverStatus === DELIVER_STATUS.DELIVERED || recordStatus === BenefitRecordAwardStatus.Delivered;
    // 0 未领取 1 已领取
    if (hasDelivered && prizeType !== AWARD_PRIZE_TYPE.CDKEY) {
      report(ATTA_REPORT.USER_REWARD.MYREWARD_RECEIVED_CLICK, {
        prizeId: prizeId || productId,
        prizeType,
        prizeSource,
      });
      handleReceiveGiftSuccess();
      return;
    }
    report(ATTA_REPORT.USER_REWARD.MYREWARD_NOTRECEIVED_CLICK, {
      prizeId: prizeId || productId,
      prizeType,
      prizeSource,
    });
    switch (prizeType) {
      // 游戏道具
      case AWARD_PRIZE_TYPE.AMS: {
        // ams礼包领取
        const lotteryAmsPrizeDelivery = async (params) => {
          const res =
            prizeSource === PrizeSource.FRAGMENT_FUSION
              ? await awardFusionPrize({ ...params, fusionPrizeRecordId })
              : await fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
          if (res?.code !== 0) {
            // 所有的领取失败
            monitor.reportLog({
              actionName: 'prizeDeliverfailed',
            });
            report(ATTA_REPORT.LOTTERY_REACEIVE.AMS_RECEIVE_FAIL, {
              prizeSource,
            });
            TipsModalV2.show({
              title: ERROR_MSG[res?.code] || '礼包领取失败',
              content: ERROR_MSG[res?.code] ? '' : '请稍后重试',
            });
            return;
          }
          successCallback?.();
          report(ATTA_REPORT.LOTTERY_REACEIVE.AMS_RECEIVE_SUCCESS, {
            prizeSource,
          });
          return handleReceiveGiftSuccess();
        };
        this.displayMobileGameRole(lotteryAmsPrizeDelivery, {});
        return;
      }

      // 实物奖品
      case AWARD_PRIZE_TYPE.GOODS: {
        report(ATTA_REPORT.LOTTERY_REACEIVE.GOODS_RECEIVEDIALOG_SHOW, { prizeSource });
        showOrderAddressInput({ ...prizeInfo, successCallback });
        return;
      }

      // QB
      case AWARD_PRIZE_TYPE.QB: {
        // QB礼包领取
        const lotteryQBPrizeDelivery = async (params: QBDeliverParams) =>
          prizeSource === PrizeSource.FRAGMENT_FUSION
            ? await awardFusionPrize({ ...params, fusionPrizeRecordId })
            : await fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
        showReceiveQBDialog({ deliverRequest: lotteryQBPrizeDelivery, receiveCallback: successCallback });

        return;
      }
      case AWARD_PRIZE_TYPE.CASH: {
        receiveCash({ prizeInfo, callback: successCallback, prizeSource });
        return;
      }

      case AWARD_PRIZE_TYPE.CDKEY: {
        receiveCdkey({ prizeInfo, callback: successCallback, actId, lotteryId });
        return;
      }
    }
    async function handleReceiveGiftSuccess() {
      const reportEvent = () => {
        report(ATTA_REPORT.USER_LOTTERY.LOTTERY_CLOSE_MODAL, { prizeSource });
      };
      switch (prizeType) {
        // 游戏道具
        case AWARD_PRIZE_TYPE.AMS: {
          TipsModal.show({
            title: '温馨提示',
            content: [`已成功领取${prizeName}`, '已发送至游戏邮箱，请在游戏内查收'],
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });
          return;
        }

        // 实物奖品
        case AWARD_PRIZE_TYPE.GOODS: {
          TipsModal.show({
            content: SaveAddressSuccess,
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });
          return;
        }

        case AWARD_PRIZE_TYPE.OUTER_LINK: {
          showOrderJumpUrlModal(prizeInfo);
          return;
        }

        case AWARD_PRIZE_TYPE.QB: {
          TipsModal.show({
            title: '温馨提示',
            content:
              deliverStatus === 1 ? ['已领取', '请前往领取的QQ号查看是否到账'] : ['24小时内Q币可到账', '请注意查收'],
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });
          return;
        }
        case AWARD_PRIZE_TYPE.CASH: {
          TipsModal.show({
            title: '温馨提示',
            content: ['已领取', '请前往领取的QQ号查看是否到账'],
            confirmText: '我知道了',
            confirmCallback: reportEvent,
          });

          return;
        }
        case AWARD_PRIZE_TYPE.FRAGMENT: {
          // TOOD chengdong
          return;
        }

        case AWARD_PRIZE_TYPE.MARKETING_RESOURCE: {
          receiveMarketingResourcePrize({
            prizeId: prizeInfo.prizeId,
          });
          return;
        }
      }
    }
  }

  private async getUserPrizeStatus(actId: number): Promise<number> {
    const res = await fetchUserPrizeStatus({ actId });
    if (res?.code !== 0) {
      return 0;
    }
    const { user_prizes: userPrizes } = res?.data || {};
    const receivedPrizes = userPrizes?.filter((c) => c?.up_deliver_status === 1) || [];
    if (!receivedPrizes.length) return 0;
    // const { prize_id: prizeId } = receivedPrizes[0] || {};
    return actId;
    // this.userReceivePrizeStatus[actId] = {
    //   prizeId,
    // }
    // const detailRes = await fetchPrizeDetail({ actId });
    // if (detailRes?.code !== 0) {
    //   return true;
    // };
    // const { prize_groups: prizeGroups } = res?.data || {}
    // const prizeName = prizeGroups?.[0]?.prizes?.[0].prize_name;
    // this.userReceivePrizeStatus[actId] = {
    //   ...this.userReceivePrizeStatus[actId],
    //   prizeName
    // }
  }

  // 领取抽奖的奖品
  private receiveLotteryGift(prizeInfo: {
    actId: number;
    prizeType: number;
    awardName: string;
    prizeId?: string;
    lotteryId?: string;
    prizeUrl?: string;
  }) {
    const { actId, prizeType, awardName, prizeId, lotteryId } = prizeInfo;
    switch (prizeType) {
      // 游戏道具
      case AWARD_PRIZE_TYPE.AMS: {
        TipsModal.show({
          content: `恭喜您获得${awardName}`,
          confirmCallback: () =>
            this.displayMobileGameRole(this.receiveGiftSuccess.bind(this), { actId, lotteryId }, true),
        });
        return;
      }

      // 实物奖品
      case AWARD_PRIZE_TYPE.GOODS: {
        showTipsDialog({
          title: '恭喜中奖',
          content: `恭喜您获得${awardName}，请填写收货地址`,
          buttonText: '填写地址',
          onConfirm: () => {
            showOrderAddressInput({
              actId,
              prizeId,
              lotteryId,
              successCallback: () => this.awardPrizeStore.changePrizeStatus([actId]),
            });
          },
        });
        return;
      }

      // 谢谢参与
      case AWARD_PRIZE_TYPE.NOTHING: {
        TipsModal.show({
          content: '很遗憾未抽中奖品，感谢您的参与',
        });
        // 更新领取状态
        this.awardPrizeStore.changePrizeStatus([actId]);
        return;
      }

      // 游戏币
      case AWARD_PRIZE_TYPE.GAME_CURRENCY: {
        const giftData = this.allGiftData.find((c) => c.actId === actId);
        // 如果是权益礼包抽到了游戏币 则直接到账
        const title = giftData?.giftType === AWARD_TYPES.RIGHTS ? `已领取成功${awardName}` : `恭喜获得${awardName}`;
        TipsModal.show({
          title,
          content: '请前往游戏币兑换专区查看',
        });
        // 更新领取状态
        this.awardPrizeStore.changePrizeStatus([actId]);
        return;
      }

      case AWARD_PRIZE_TYPE.MARKETING_RESOURCE: {
        receiveMarketingResourcePrize({
          prizeId,
        });
        return;
      }

      case AWARD_PRIZE_TYPE.QB: {
        const multipleLotteryQBPrizeDelivery = async (params: QBDeliverParams) =>
          fetchReceiveLotteryPrize({ ...params, actId, lotteryId });
        showReceiveQBDialog({
          deliverRequest: multipleLotteryQBPrizeDelivery,
          receiveCallback: () => {
            this.awardPrizeStore.changePrizeStatus([actId]);
          },
        });
        return;
      }

      case AWARD_PRIZE_TYPE.OUTER_LINK: {
        showOrderJumpUrlModal({ ...prizeInfo, prizeName: awardName });
        return;
      }
    }
  }

  private async lotteryGiftRequest(params: GiftDataType, giftType) {
    const res = await sigleDraw({ activityId: params.actId as number });
    report(giftType === AWARD_TYPES.LOTTERY ? ATTA_REPORT.LOTTERY_PRIZE.LOTTERY : ATTA_REPORT.LOTTERY_PRIZE.RIGHT, {
      eventResult: res?.code === 0 ? ATTA_EVENT_RESULT.SUCCESS : res?.code,
    });
    if (res?.code !== 0) {
      giftType === AWARD_TYPES.LOTTERY && event.emit(EVENT.INVOKE_LOTTERY_END);
      this.handleReceiveGiftFail(res?.code, params, true);
      return;
    }
    const { prizeType, prizeId, prizeName: awardName, lotteryId, prizeUrl } = res?.data?.drawResult?.[0];
    return { prizeType, awardName, prizeId, lotteryId, prizeUrl };
  }

  private async receiveGiftResponse(res) {
    const prizes = res?.data?.prize_groups?.[0]?.prizes || [];
    const prizeType: number = prizes?.[0].prize_type;
    const prizeId = prizes?.[0].prize_id;
    const awardName = prizes.map((item) => item.prize_name || '礼包').join('、');

    return { prizeType, awardName, prizeId };
  }

  private async receiveGiftSuccess(data: ReceiveGiftRequestData, isLottery?: boolean) {
    const giftInfo = this.allGiftData.find((c) => c.actId === data.actId);
    const { prizeType, awardName, prizeId } = (await this.receiveGiftRequest(data, isLottery)) || {};
    const reportKey = getReceiveGiftReportKey(giftInfo?.giftType);
    report(reportKey, {
      eventResult: prizeType ? ATTA_EVENT_RESULT.SUCCESS : ATTA_EVENT_RESULT.FAIL,
    });
    if (!prizeType) return;
    const title = `已成功领取${awardName}`;
    let content = '已发送至游戏邮箱，请在游戏内查收';
    if (prizeType === AWARD_PRIZE_TYPE.GAME_CURRENCY) {
      content = '游戏币已发放到当前订购的手机号，请跳转兑换页查看并兑换道具';
    }
    const receiveGiftData = this.allGiftData.find((c) => c.actId === data.actId);
    const receivedList = this.awardPrizeStore.changePrizeStatus([data.actId]);
    if (prizeType === AWARD_PRIZE_TYPE.MARKETING_RESOURCE) {
      receiveMarketingResourcePrize({
        prizeId,
      });
      return;
    }
    // 如果是不支持订购的，领取普通礼包则弹出二维码关注跳转小程序
    if (receiveGiftData?.giftType === AWARD_TYPES.NORMAL) {
      const guideConfig = gameCardGuideService.getNoGiftGuideConfig('resultmodal');
      report(ATTA_REPORT.UNSUPPORTED_MODAL.RESULT_MODAL_SHOW);
      showModal(
        showReceiveDone({
          title,
          confirmButtonText: guideConfig?.confirmText,
          confirmCallback: guideConfig?.confirmCallback,
          showClose: !!guideConfig?.footer,
          footer: guideConfig?.footer,
        }),
      );
      return;
    }
    const needReceiveGiftList = this.getRemainNeedReceiveList(receivedList);

    // 存在用户同时领取普通礼包以及抽奖、权益必得礼包，但是判断是否已领取完，只需要判断除普通礼包之外所有礼包都有没有领完
    if (!needReceiveGiftList.length) {
      // 已完成
      showModal(showReceiveDone({ title }));
      return;
    }
    TipsModal.show({
      title,
      content,
      confirmText: '继续领取',
      confirm: () => {
        const giftData = needReceiveGiftList[0];
        const element = document.getElementById(`gift-${giftData.actId}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
    });
  }

  private async receiveGiftRequest(params: ReceiveGiftRequestData, isLottery?: boolean) {
    const apiRequest = isLottery ? fetchReceiveLotteryPrize : fetchReceiveGift;
    const res = await apiRequest(params);

    if (res?.code !== 0) {
      this.handleReceiveGiftFail(res?.code, params);
      return;
    }

    return this.receiveGiftResponse(res);
  }

  // 展示弹窗区分：必得、抽奖
  private displayMobileGameRole(callback, params, isLottery?: boolean) {
    return showModal(
      getMobileGameRole({
        confirmCallback: (roleData: GameRoleType) => callback({ ...params, ...roleData }, isLottery),
      }),
    );
  }

  // 处理领奖失败的情况
  private handleReceiveGiftFail(code, params: ReceiveGiftRequestData | GiftDataType, isLottery?: boolean) {
    const userOrderInfo = userOrderService.getUserOrderInfo();
    switch (code) {
      case RECEIVE_ERROR_CODE.CMCC_NO_AWARD_CHANCE: {
        // 如果是抽奖失败，要区分权益礼包还是抽奖礼包，抽奖礼包不支持异步发货
        if (isLottery) {
          const giftData = params?.actId ? this.allGiftData.find((c) => c.actId === params?.actId) : null;
          if (giftData?.giftType === AWARD_TYPES.RIGHTS) {
            this.displayMobileGameRole(this.asyncDeliver.bind(this), {
              actId: giftData.actId,
              gameId: channelService.gameCode,
              mobile: userOrderInfo?.orderMobile,
              isOverYear: userOrderInfo?.isOverYear,
            });
            return;
          }
          // 如果订购时间大于12个月则展示新增次数弹窗，否则直接跳转小程序订阅消息
          return handleCMCCPrizeReceive(userOrderInfo?.orderMobile, channelService.gameCode, {
            isOverYear: userOrderInfo?.isOverYear,
          });
        }
        this.asyncDeliver({
          ...params,
          gameId: channelService.gameCode,
          mobile: userOrderInfo?.orderMobile,
          isOverYear: userOrderInfo?.isOverYear,
        });
        break;
      }

      case RECEIVE_ERROR_CODE.CMCC_LIMIT: {
        handleCMCCPrizeReceive(userOrderInfo?.orderMobile, channelService.gameCode);
        break;
      }

      default:
        TipsModal.show({
          title: ERROR_MSG[code] || '礼包领取失败',
          content: ERROR_MSG[code] ? '' : '请稍后重试',
        });
    }
  }

  private async asyncDeliver(params) {
    // 判断是否有异步发货记录
    const res = await fetchAsyncDeliveryRecord({ actId: parseInt(params.actId, 10) });
    if (res?.code !== 0) return;
    // 异步发货记录存在
    const roleInfo = res?.data || {};
    if (!roleInfo.roleId) {
      return showModal(showAsyncDeliverDialog(params));
    }
    // 如果订购时间大于12个月则展示新增次数弹窗，否则直接跳转小程序订阅消息
    return handleCMCCPrizeReceive(params.mobile, params.gameId, {
      isAuto: true,
      roleInfo: `${roleInfo.partitionName ? `${roleInfo.partitionName}-` : ''}${roleInfo.roleName}`,
      isOverYear: params.isOverYear,
    });
  }

  private getRemainNeedReceiveList(receivedList) {
    return this.allGiftData.filter((c) => !receivedList.includes(c.actId) && c.giftType !== AWARD_TYPES.NORMAL);
  }
}

const awardPrizeService = new AwardPrizeService();
export default awardPrizeService;
